import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router";
import { CopyButton } from "../../components/CopyButton";
import { Icon } from "../../components/icons";
import { CopyField } from "../../components/CopyField";
import {
  Alert,
  Card,
  CardHeader,
  EmptyState,
  LoadingBlock,
  Mono,
  StatusBadge,
  buttonStyles,
  cx,
} from "../../components/ui";
import { UsageChart } from "../../components/UsageChart";
import {
  EMPTY_TOTALS,
  SNIPPETS,
  USAGE_SERIES,
  inferenceBaseUrl,
  partitionAiModels,
  usageSeries,
  type SnippetLanguage,
  type UsageSeriesKey,
} from "../../lib/inference";
import { formatUsageCost } from "../../lib/money";
import {
  useAiModels,
  useFreeInference,
  useInferenceUsage,
} from "../../lib/queries";
import { useAiTabContext } from "./context";
import {
  dismissalKey,
  isDismissed,
  readDismissed,
  rememberDismissal,
} from "./freeInferenceDismissal";
import { Panel, Segmented, ShareRule } from "./parts";
import { StatStrip } from "./StatStrip";

const TOP_MODELS = 5;

/**
 * The tab somebody lands on, which has two entirely different jobs depending on
 * whether they have ever made a call.
 *
 * Before the first request there is nothing to report and one thing to do, so the
 * quickstart is the page. After it, the quickstart is reference material somebody
 * has already used and the numbers are what they came back for — so it collapses
 * to a summary line and the month's figures take the top.
 *
 * That ordering swap is the whole design of this tab. A fixed layout has to pick
 * one of the two audiences to disappoint: lead with the quickstart forever and a
 * returning customer scrolls past a wall of code to find their spend; lead with
 * the numbers and a new one lands on four zeros and an empty chart.
 */
/**
 * A token count as a person reads it. Floored, never rounded up: every token
 * figure the platform publishes is a floor, and a display that rounded would
 * undo that at the last step.
 */
function compactTokens(n: number): string {
  if (n >= 1_000_000_000_000)
    return `${(Math.floor(n / 100_000_000_000) / 10).toFixed(1)}T`;
  if (n >= 1_000_000_000) return `${(Math.floor(n / 100_000_000) / 10).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(Math.floor(n / 100_000) / 10).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.floor(n / 1_000)}K`;
  return String(n);
}

export function OverviewTab() {
  const { t } = useTranslation("ai");
  const { orgId } = useAiTabContext();
  const models = useAiModels();
  const usage = useInferenceUsage(orgId);
  const offer = useFreeInference(orgId);

  const [language, setLanguage] = useState<SnippetLanguage>("curl");
  const [series, setSeries] = useState<UsageSeriesKey>("spend");
  const activeSeries = usageSeries(series);

  const listed = models.data ?? [];
  const { featured, regular } = partitionAiModels(listed);
  /*
   * A global featured rank is the catalogue's explicit quickstart choice. The
   * fallback keeps the snippet copyable while the list loads or is empty, and it
   * is a placeholder rather than a real id: a hard-coded id here is a second copy
   * of the catalogue that nothing keeps in step, and the one that used to be here
   * outlived the model it named.
   */
  const exampleModel = featured[0]?.id ?? regular[0]?.id ?? "MAXLAYER_MODEL_ID";

  const spend = usage.data;
  const currency = spend?.currency ?? "USD";
  const hasTraffic = (spend?.monthToDate.requests ?? 0) > 0;
  const topModels = (spend?.byModel ?? []).slice(0, TOP_MODELS);
  const rangeCost = spend?.rangeTotals.costMicros ?? 0;

  const quickstart = (
    <Card>
      {/* The key half of this used to be here as well as in the alert below.
          One of the two was redundant, and the alert is the one that can link. */}
      <CardHeader
        title={t("overview.getStarted")}
        description={t("overview.getStartedDescription")}
      />

      <div className="space-y-4 p-5">
        <div className="space-y-1.5">
          <p className="micro">{t("overview.baseUrl")}</p>
          <CopyField value={inferenceBaseUrl()} label={t("overview.baseUrlLabel")} />
        </div>

        {/*
          The key is deliberately never shown here. Keys are displayed once, at
          creation; printing one on a page any viewer can open would put it on
          screen long after the moment it was meant to be visible. The snippet
          carries an environment variable instead, and this links to where a key
          is made.

          "not your login" is the part that cannot be cut. A session token looks
          like a credential and is the one a customer has to hand, and the failure
          it produces is a 401 that reads as "the endpoint is broken".
        */}
        <Alert tone="info">
          <Trans
            ns="ai"
            i18nKey="overview.useOrgKey"
            components={{
              key: <span className="font-medium" />,
              create: <Link to="/settings/api-keys" className="text-accent underline underline-offset-2" />,
              env: <Mono />,
            }}
          />
        </Alert>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-4">
            <Segmented
              label={t("overview.snippetLanguage")}
              value={language}
              onChange={setLanguage}
              options={(Object.keys(SNIPPETS) as SnippetLanguage[]).map((key) => ({
                value: key,
                label: SNIPPETS[key].label,
              }))}
            />
            <CopyButton
              value={SNIPPETS[language].build(exampleModel)}
              label={`Copy the ${SNIPPETS[language].label} example`}
            />
          </div>

          <pre className="overflow-x-auto rounded-lg border border-line bg-canvas p-4 font-mono text-xs leading-relaxed text-fg-muted">
            {SNIPPETS[language].build(exampleModel)}
          </pre>
        </div>

        {/*
          Worth saying explicitly rather than leaving people to infer it from
          "OpenAI-compatible". The base URL above is the whole configuration for
          every one of these, and somebody evaluating the product wants to know
          their editor works before they read an API reference.
        */}
        <p className="text-xs leading-6 text-fg-subtle">
          <Trans
            i18nKey="overview.works"
            ns="ai"
            components={{ tools: <span className="text-fg-muted" /> }}
          />
        </p>
      </div>
    </Card>
  );

  const freeOffer = offer.data?.data;
  const request = freeOffer?.request ?? null;

  /*
   * Read once on mount rather than on every render: this is a synchronous
   * `localStorage` hit, and the answer only changes when the button below is
   * pressed — which updates the state itself.
   */
  const [dismissed, setDismissed] = useState(readDismissed);
  const hidden = isDismissed(dismissed, orgId, request?.status);

  /*
   * Where an application got to, for the organizations that made one.
   *
   * Nothing to apply with, because there is nowhere here to apply from: the
   * programme is for new accounts and the ask lives on the registration form.
   * An organization that did not apply on the way in sees no card at all —
   * advertising an offer beside no way to take it is worse than silence.
   *
   * The verdict is the card's identity rather than a box at the bottom of it.
   * It read as a heading, a paragraph of prose, and then — underneath both, in
   * the least prominent position on the card — the one fact somebody opened it
   * to find. Status now sits in the header beside the name, the facts of the
   * offer are three things to scan rather than one sentence to parse, and an
   * approval ends somewhere to go: a key in an inbox with no next step is a
   * dead end, and the endpoint it calls is already on this page.
   */
  const campaignState = request
    ? ({
        requested: {
          tone: "warn" as const,
          label: t("overview.request.reviewing"),
          line: t("overview.request.reviewingLine"),
        },
        approved: {
          tone: "good" as const,
          label: t("overview.request.approved"),
          /* Who got it, because the person reading may not be one of them, and
             that it will not come again, so they go and find it. */
          line: t("overview.request.issuedLine"),
        },
        rejected: {
          tone: "neutral" as const,
          label: t("overview.request.rejected"),
          line:
            request.note ?? t("overview.request.rejectedLine"),
        },
      }[request.status] ?? null)
    : null;

  const campaignCard =
    request && campaignState && !hidden ? (
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-5 py-4">
          <div className="min-w-0">
            <p className="micro text-fg-subtle">{t("overview.request.freeInference")}</p>
            <h3 className="mt-1 text-fg">{freeOffer!.name}</h3>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <StatusBadge tone={campaignState.tone} label={campaignState.label} />
            {/*
              Closing a finished message. The verdict is in the key, so this
              hides what has been read without hiding what comes next — somebody
              who closes "under review" still meets the decision.
            */}
            <button
              type="button"
              onClick={() => {
                if (!orgId) return;
                setDismissed(rememberDismissal(dismissalKey(orgId, request.status)));
              }}
              /* 44px under a thumb, per the small-screens rule. It sits at the
                 end of its row with no neighbour to steal a tap from. */
              className="-my-1 -mr-2 grid size-8 shrink-0 place-items-center rounded-md text-fg-subtle transition-colors duration-200 ease-smooth hover:bg-fg/8 hover:text-fg pointer-coarse:size-11"
            >
              <span className="sr-only">
                {request.status === "requested"
                  ? t("overview.request.hide")
                  : t("overview.request.dismiss")}
              </span>
              <Icon name="close" className="size-3.5" />
            </button>
          </div>
        </div>

        <div className="space-y-5 px-5 py-4">
          <p className="max-w-prose text-sm leading-6 text-fg-muted">{campaignState.line}</p>

          {/*
            The offer as three facts rather than one sentence carrying all of
            them. It stays while the allowance is still something this
            organization might get, and goes on a refusal — describing a key
            somebody has just been told they are not getting reads as a taunt.
          */}
          {request.status !== "rejected" && (
            <dl className="grid gap-px overflow-hidden rounded-md border border-line bg-line sm:grid-cols-3">
              <div className="bg-surface px-4 py-3">
                <dt className="micro text-fg-subtle">{t("overview.request.everyMonth")}</dt>
                <dd className="mt-1 text-sm text-fg tabular-nums">
                  {freeOffer!.tokensPerKey === null
                    ? t("overview.request.allowance")
                    : t("overview.request.tokens", {
                        tokens: compactTokens(freeOffer!.tokensPerKey),
                      })}
                </dd>
              </div>
              <div className="bg-surface px-4 py-3">
                <dt className="micro text-fg-subtle">
                  {t("overview.request.model", { count: freeOffer!.models.length })}
                </dt>
                {/* Wrapped rather than truncated. This is the string somebody
                    pastes into their client, and half of it is no use. */}
                <dd className="mt-1 text-sm break-all text-fg">
                  {freeOffer!.models.length === 1 ? (
                    <Mono>{freeOffer!.models[0]}</Mono>
                  ) : (
                    t("overview.request.modelCount", { count: freeOffer!.models.length })
                  )}
                </dd>
              </div>
              <div className="bg-surface px-4 py-3">
                <dt className="micro text-fg-subtle">{t("overview.request.billedToYou")}</dt>
                <dd className="mt-1 text-sm text-fg">{t("overview.request.nothing")}</dd>
              </div>
            </dl>
          )}

          {/*
            Somewhere to go, and a different somewhere per verdict. Approved
            people have a credential and no idea what to point it at; refused
            ones want the thing they came for, which they can still have by
            paying for it. Somebody waiting has nothing to do, so they are given
            nothing to press.
          */}
          {request.status === "approved" && (
            <div className="flex flex-wrap items-center gap-3">
              <Link to="models" relative="path" className={buttonStyles("secondary")}>
                {t("overview.browseModels")}
              </Link>
              <p className="text-xs leading-5 text-fg-subtle">
                {t("overview.quickstart")}
              </p>
            </div>
          )}
          {request.status === "rejected" && (
            <div className="flex flex-wrap items-center gap-3">
              <Link to="/settings/billing" className={buttonStyles("secondary")}>
                {t("overview.addCredit")}
              </Link>
              <p className="text-xs leading-5 text-fg-subtle">
                {t("overview.sameEndpoint")}
              </p>
            </div>
          )}
        </div>
      </Card>
    ) : null;

  if (usage.isLoading) return <LoadingBlock />;

  if (usage.isError) {
    return (
      <div className="space-y-6">
        <Alert>{t("usageLoadFailed")}</Alert>
        {campaignCard}
        {quickstart}
      </div>
    );
  }

  /*
   * No traffic: the quickstart is the page, and everything below it would be four
   * zeros and an empty chart. The one figure worth keeping is the catalogue size,
   * which answers "is there anything here to call" — and it links to the models
   * tab, which is where somebody goes next.
   */
  if (!hasTraffic) {
    return (
      <div className="space-y-6">
        {campaignCard}
        {quickstart}

        <Card>
          <EmptyState
            icon="models"
            title={t("overview.empty")}
            description={t("overview.emptyDescription")}
            action={
              <Link to="models" relative="path" className={buttonStyles("secondary")}>
                {listed.length > 0
                  ? t("overview.browseCount", { count: listed.length })
                  : t("overview.browse")}
              </Link>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/*
        Here too, and this is the branch that matters most.

        The card was on the empty and error states alone, from when it was an
        *offer* and the person to show it to was somebody with nothing to call.
        As a status display that is backwards: an approved applicant is using
        the key, so they have traffic, so the one sentence telling them where it
        was sent disappeared the moment it became true.
      */}
      {campaignCard}

      <StatStrip
        totals={spend?.monthToDate ?? EMPTY_TOTALS}
        currency={currency}
        caption={t("overview.thisMonth")}
        stale={usage.isFetching}
      />

      {/*
        The chart's window and the strip's are different — 30 days against the
        calendar month — and each says which it is. Matching them would mean a
        chart with two bars in it on the 2nd.
      */}
      <Panel
        title={t("overview.lastDays", { count: spend?.range.days ?? 30 })}
        action={
          <Segmented
            label={t("overview.chartSeries")}
            value={series}
            onChange={setSeries}
            options={USAGE_SERIES.map((option) => ({
              value: option.key,
              label: t(`series.${option.key}`),
            }))}
          />
        }
      >
        <div className="p-5">
          <UsageChart
            points={(spend?.days ?? []).map((day) => ({
              day: day.day,
              value: activeSeries.value(day),
            }))}
            format={(value) => activeSeries.format(value, currency)}
            label={t("overview.daily", {
              series: t(`series.${activeSeries.key}`).toLocaleLowerCase(),
            })}
            emptyLabel={t("overview.noRequests")}
          />
        </div>
      </Panel>

      <Panel
        title={t("overview.topModels", { count: spend?.range.days ?? 30 })}
        action={
          <Link
            to="activity"
            relative="path"
            className="text-xs text-accent transition-colors duration-200 ease-smooth hover:text-accent-hover"
          >
            {t("overview.fullActivity")}
          </Link>
        }
      >
        {topModels.length === 0 ? (
          <p className="px-4 py-6 text-sm text-fg-subtle">
            {t("overview.widerPeriod")}
          </p>
        ) : (
          <ul className="divide-y divide-line">
            {topModels.map((row) => (
              <li key={row.model} className="px-4 py-3">
                <div className="flex items-baseline justify-between gap-4">
                  <Mono className="truncate text-xs text-fg">{row.model}</Mono>
                  <span className="shrink-0 font-mono text-xs tabular-nums text-fg-muted">
                    {formatUsageCost(row.costMicros, currency)}
                  </span>
                </div>
                <ShareRule value={row.costMicros} total={rangeCost} />
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {/*
        Collapsed, not removed. Somebody wiring up a second service needs the base
        URL again, and the alternative — sending them to the docs site for a string
        this page already knows — is worse than a disclosure triangle.
      */}
      <details className="group rounded-lg border border-line bg-surface">
        <summary
          className={cx(
            "flex cursor-pointer items-center justify-between gap-4 px-4 py-3 text-sm text-fg-muted",
            "transition-colors duration-200 ease-smooth hover:text-fg",
          )}
        >
          <span>
            {t("overview.endpoint")}
            <span className="ml-2 font-mono text-xs text-fg-subtle">{inferenceBaseUrl()}</span>
          </span>
          <span className="text-xs text-fg-subtle group-open:hidden">{t("overview.show")}</span>
          <span className="hidden text-xs text-fg-subtle group-open:inline">{t("overview.hide")}</span>
        </summary>
        <div className="border-t border-line">{quickstart}</div>
      </details>

      {/*
        A charge lands on the balance hourly, so the figures above can be up to an
        hour ahead of what has actually been deducted. Saying so is cheaper than
        fielding the question.

        Traffic that cost nothing gets a sentence of its own, because four
        thousand requests beside "Spent $0.00" reads as a broken meter otherwise.
        Both causes are named and neither is guessed at: this page knows the
        total is zero, not which of the two made it so.
      */}
      <p className="text-xs text-fg-subtle">
        {(spend?.monthToDate.costMicros ?? 0) === 0 && (
          <>
            {t("overview.nothingCharged")}{" "}
          </>
        )}
        {t("overview.metered")}
      </p>
    </div>
  );
}
